import { useRef, useEffect } from "react";
import { Link } from "wouter";
import { BlurFade } from "@/components/ui/blur-fade";
import { GridPattern } from "@/components/ui/grid-pattern";
import { cn } from "@/lib/utils";

/* ── "Our Take on AI" Symbol ─────────────────────────────────── */

const AI_ITEMS: Array<{ text: string; href?: string }> = [
  { text: "SHERPA. Our proprietary AI measurement platform." },
  { text: "AI-powered bidding and budget optimization" },
  { text: "Creative production at machine speed" },
  { text: "AI transformation training for your team", href: "/ai-training-for-teams" },
];

export interface AiTakeSectionProps {
  className?: string;
}

export function AiTakeSection({ className }: AiTakeSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mobileVideoRef = useRef<HTMLVideoElement>(null);

  // Play/pause both video copies when section enters/leaves viewport. Combined
  // with preload="none" on each <video>, this means the 1.7 MB MP4 only ever
  // starts downloading once the user actually scrolls to this section —
  // saving ~1.7 MB on every initial mobile page load (the video previously
  // had preload="auto" + autoplay which downloaded eagerly on every visit).
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        const desktop = videoRef.current;
        const mobile = mobileVideoRef.current;
        if (entry.isIntersecting) {
          desktop?.play().catch(() => {});
          mobile?.play().catch(() => {});
        } else {
          desktop?.pause();
          mobile?.pause();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(section);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`relative bg-cream overflow-hidden ${className ?? ""}`}
      data-testid="section-ai-take"
    >
      {/* Grid lines — visible top-right, fading toward bottom-left */}
      <GridPattern
        width={24}
        height={24}
        x={-1}
        y={-1}
        className={cn(
          "z-[1] fill-transparent stroke-[hsla(60,3%,8%,0.14)]",
          "[mask-image:linear-gradient(to_top_right,transparent_20%,rgba(0,0,0,0.4)_50%,rgba(0,0,0,0.8)_100%)]",
        )}
      />

      {/* Video — absolute, flush to right browser edge, above divider */}
      <div
        className="hidden lg:flex absolute right-0 items-end pointer-events-none"
        style={{ top: 0, bottom: 3, width: "45vw" }}
      >
        <video
          ref={videoRef}
          src="/assets/video/robot-scroll.mp4"
          muted
          loop
          playsInline
          preload="none"
          className="w-full h-full object-contain object-right-bottom"
          style={{ mixBlendMode: "darken", display: "block" }}
        />
      </div>

      <div
        className="container-content relative z-10"
        style={{
          paddingTop: "var(--space-section-top)",
          paddingBottom: "var(--space-section-bottom)",
        }}
      >
        <div className="lg:w-[60%]">
          <BlurFade inView delay={0.1}>
            <h2
              className="type-h1 italic uppercase"
              style={{ color: "var(--charcoal)" }}
            >
              Our Take on AI
            </h2>
          </BlurFade>

          <BlurFade inView delay={0.15}>
            <p
              className="type-body"
              style={{
                color: "var(--charcoal)",
                marginTop: "var(--space-gap-sm)",
              }}
            >
              AdVenture Media is an AI-first agency. Full stop. We embrace it,
              we build with it, and we've helped our clients transform their
              operations and profitability with AI. Faster optimization cycles.
              Sharper data. Creative testing at a pace humans can't match alone.
              Agencies who are not adopting AI are only disadvantaging their
              clients and their own people.
            </p>
          </BlurFade>

          <BlurFade inView delay={0.2}>
            <div
              className="grid grid-cols-1 sm:grid-cols-2"
              style={{
                columnGap: "var(--space-gap-md)",
                rowGap: "var(--space-gap-sm)",
                marginTop: "var(--space-gap-md)",
              }}
            >
              {AI_ITEMS.map((item) => (
                <div
                  key={item.text}
                  className="flex items-start"
                  style={{ gap: 8 }}
                >
                  <span
                    className="flex-shrink-0"
                    style={{
                      color: "var(--terra)",
                      fontFamily: "var(--font-sans)",
                      fontSize: 24,
                      fontWeight: 300,
                      lineHeight: 1,
                    }}
                  >
                    +
                  </span>
                  {item.href ? (
                    <Link
                      href={item.href}
                      className="type-body underline underline-offset-4 decoration-[var(--terra)] hover:text-terra transition-colors"
                      style={{ color: "var(--charcoal)" }}
                    >
                      {item.text}
                    </Link>
                  ) : (
                    <span
                      className="type-body"
                      style={{ color: "var(--charcoal)" }}
                    >
                      {item.text}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </BlurFade>
        </div>

        {/* Mobile video — below content. preload="none" + viewport-gated play
            keeps the 1.7 MB MP4 off the critical mobile path. */}
        <div className="lg:hidden flex justify-center" style={{ marginTop: "var(--space-gap-lg)" }}>
          <video
            ref={mobileVideoRef}
            src="/assets/video/robot-scroll.mp4"
            loop
            muted
            playsInline
            preload="none"
            className="w-full max-w-[280px] object-contain"
            style={{ mixBlendMode: "darken", display: "block" }}
          />
        </div>
      </div>

      {/* Full-width bottom divider */}
      <div className="relative z-20" style={{ borderBottom: "var(--border-light-heavy)" }} />
    </section>
  );
}
